import React, { useState } from 'react';
import styled from '@emotion/styled';
import { FandomConcept } from '../types';
import { conceptToQuery } from '../utils/conceptUtils';
import { memoryService } from '../services/memoryService';
import { mixpanelService } from '../services/mixpanel';
import { activityTracker } from '../services/activityTracker';
import { rateLimiter } from '../services/rateLimiter';

interface AdvancedConceptCardProps {
  concept: FandomConcept;
  parentQuery: string;
  onExplore: (query: string) => void;
  isClicked?: boolean;
  isLoading?: boolean;
}

const Card = styled.button<{ isHovered?: boolean; isClicked?: boolean; isLoading?: boolean }>`
  background: var(--color-surface);
  border: 1px solid rgba(var(--color-border-rgb, 51, 65, 85), 0.3);
  border-radius: var(--radius-sm);
  padding: var(--gap-md) var(--gap-lg);
  cursor: ${props => props.isClicked ? 'default' : 'pointer'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--gap-md);
  width: 100%;
  text-align: left;
  font-family: inherit;
  box-shadow: var(--shadow-sm);
  min-height: 44px;
  overflow: visible;
  pointer-events: ${props => props.isClicked ? 'none' : 'auto'};



  &:hover:not([disabled]) {
    transform: ${props => props.isClicked ? 'none' : 'translateY(-2px)'};
    background: var(--color-surface-hover);
    box-shadow: ${props => props.isClicked ? 'var(--shadow-sm)' : 'var(--shadow-lg)'};
  }

  &:active:not([disabled]) {
    transform: ${props => props.isClicked ? 'none' : 'scale(0.98)'};
    box-shadow: var(--shadow-sm);
  }

  span.arrow {
    opacity: ${props => props.isHovered ? '0.8' : '0.4'};
    transform: ${props => props.isHovered ? 'translateX(4px)' : 'translateX(0)'};
  }
  
  @media (max-width: 768px) {
    padding: var(--gap-lg);
    min-height: 56px;
    border-radius: var(--radius-md);
    
    &:hover:not([disabled]) {
      transform: none;
    }
    
    &:active:not([disabled]) {
      background: var(--color-surface-hover);
      transform: ${props => props.isClicked ? 'none' : 'scale(0.98)'};
    }

  }
  
  /* Better touch feedback for mobile */
  @media (hover: none) {
    &:hover:not([disabled]) {
      transform: none;
      background: ${props => props.isClicked ? 'var(--color-surface-hover)' : 'var(--color-surface)'};
      box-shadow: var(--shadow-sm);
    }
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  position: relative;
  z-index: 2;
`;

const ConceptTitle = styled.span`
  font-family: var(--font-body);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-normal);
  color: var(--color-text);
  margin: 0;
  letter-spacing: var(--letter-spacing-normal);
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: var(--font-size-sm);
    line-height: 1.5;
  }
`;

const ConceptType = styled.span`
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium, 500);
  color: var(--color-text);
  text-transform: uppercase;
  letter-spacing: var(--letter-spacing-wide);
  opacity: 0.5;
`;

const ArrowIcon = styled.span`
  font-size: var(--font-size-lg);
  color: var(--color-text);
  opacity: 0.4;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-weight: var(--font-weight-light, 300);
  flex-shrink: 0;
  position: relative;
  z-index: 2;
`;

const BorderTraceSVG = styled.svg<{ isVisible: boolean; isClicked: boolean }>`
  position: absolute;
  top: -1px;
  left: -1px;
  width: calc(100% + 2px);
  height: calc(100% + 2px);
  pointer-events: none;
  opacity: ${props => props.isVisible ? 1 : 0};
  transition: opacity 0.2s ease;
  
  rect {
    fill: none;
    stroke: var(--color-primary);
    stroke-width: 1;
    stroke-linecap: round;
    stroke-linejoin: round;
    rx: var(--radius-sm);
    
    /* Set up dash pattern for border trace */
    stroke-dasharray: 50 150;
    stroke-dashoffset: 200;
  }
  
  /* Desktop: hover animation */
  @media (hover: hover) {
    rect {
      animation: ${props => props.isVisible && !props.isClicked ? 'borderTrace 1.5s linear infinite' : 'none'};
      
      ${props => props.isClicked && `
        animation: borderTraceClick 0.4s ease-out forwards;
      `}
    }
  }
  
  /* Mobile: click animation only */
  @media (hover: none) {
    rect {
      animation: ${props => props.isClicked ? 'borderTraceClick 0.6s ease-out forwards' : 'none'};
    }
  }
  
  @keyframes borderTrace {
    0% {
      stroke-dashoffset: 200;
    }
    100% {
      stroke-dashoffset: 0;
    }
  }
  
  @keyframes borderTraceClick {
    0% {
      stroke-dashoffset: 200;
    }
    100% {
      stroke-dashoffset: 0;
    }
  }
`;

const AdvancedConceptCard: React.FC<AdvancedConceptCardProps> = ({ 
  concept, 
  parentQuery, 
  onExplore, 
  isClicked = false, 
  isLoading = false 
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const query = conceptToQuery(concept.text, parentQuery);
  
  React.useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);
  
  // Determine concept type based on the text
  const getConceptType = (text: string): string => {
    const lower = text.toLowerCase();
    if (lower.includes('theory') || lower.includes('interpretation')) return 'Theory';
    if (lower.includes('role') || lower.includes('function')) return 'Role';
    if (lower.includes('impact') || lower.includes('influence')) return 'Impact';
    if (lower.includes('comparison') || lower.includes('versus')) return 'Compare';
    if (lower.includes('?')) return 'Question';
    return 'Explore';
  };

  const handleClick = async () => {
    if (isClicked || isLoading) return;
    
    // Check rate limit first
    const rateLimitCheck = rateLimiter.canMakeRequest('concept');
    if (!rateLimitCheck.allowed) {
      // For concept cards, we'll just return silently to avoid interrupting the flow
      // The user will see the visual feedback from the disabled state
      return;
    }

    // Record the concept click request
    rateLimiter.recordRequest('concept');
    
    // Record manual activity for accurate time tracking
    activityTracker.recordActivity('concept_click');

    // Track this concept click in memory
    await memoryService.trackConceptClick(concept.text, parentQuery);
    // Track for leaderboard
    await memoryService.updateRabbitHoleStats('concept_click');
    
    // Track concept click for analytics (server-only for reliable business metrics)
    mixpanelService.track('concept_clicked', {
      concept_text: concept.text,
      concept_type: concept.type,
      parent_query: parentQuery,
      derived_query: query,
      action_type: getConceptType(concept.text),
      concept_length: concept.text.length
    });
    
    onExplore(query);
  };

  return (
    <Card 
      onClick={handleClick} 
      className="animate-hover"
      isHovered={isHovered}
      isClicked={isClicked}
      isLoading={isLoading}
      disabled={isClicked || isLoading}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <BorderTraceSVG 
        isVisible={isTouchDevice ? isClicked : (isHovered || isClicked)} 
        isClicked={isClicked}
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        <rect 
          x="0.5" 
          y="0.5" 
          width="99" 
          height="99"
        />
      </BorderTraceSVG>
      <ContentWrapper>
        <ConceptType>{getConceptType(concept.text)}</ConceptType>
        <ConceptTitle>{concept.text}</ConceptTitle>
      </ContentWrapper>
      <ArrowIcon className="arrow">→</ArrowIcon>
    </Card>
  );
};

export default AdvancedConceptCard;